import { lazy, Suspense, useMemo } from "react";
import { Route, Switch } from "wouter";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ConsentProvider } from "./contexts/ConsentContext";
import { CookieBanner } from "./components/layout/CookieBanner";
import { SkipToContent } from "./components/layout/SkipToContent";
import { PageShell } from "./components/layout/PageShell";
import { GlobalJsonLd } from "./components/SEO";
import { ScrollProgress } from "@/components/ui-sr";
import {
  buildOrganizationLD,
  buildLocalBusinessLD,
  buildWebSiteLD,
} from "./lib/seo";
import { useTracking } from "./hooks/useTracking";
import { usePerfObserver } from "./hooks/usePerfObserver";
import Home from "./pages/Home";

// Code-split rotas secundárias para não inflar o bundle inicial
const NotFound = lazy(() => import("./pages/NotFound"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const Contato = lazy(() => import("./pages/Contato"));
const Obrigado = lazy(() => import("./pages/Obrigado"));

// 6 ALPs BAZÍLIO (uma página por vertical) — lazy pra manter bundle inicial enxuto
const CVeiculos = lazy(() => import("./pages/consorcio/Veiculos"));
const CMotos = lazy(() => import("./pages/consorcio/Motos"));
const CImoveis = lazy(() => import("./pages/consorcio/Imoveis"));
const CAgro = lazy(() => import("./pages/consorcio/Agro"));
const CPesados = lazy(() => import("./pages/consorcio/Pesados"));
const CServicos = lazy(() => import("./pages/consorcio/Servicos"));

// 3 páginas persona (trio Bazilio: Camila / Lucas / Liliane)
const KCamila = lazy(() => import("./pages/consultor/Camila"));
const KLucas = lazy(() => import("./pages/consultor/Lucas"));
const KLiliane = lazy(() => import("./pages/consultor/Liliane"));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-[var(--sr-cream)] flex items-center justify-center">
      <div className="animate-pulse text-sm text-[var(--sr-navy)]/40">
        Carregando…
      </div>
    </div>
  );
}

function withShell(Component: React.ComponentType) {
  return (
    <PageShell>
      <Component />
    </PageShell>
  );
}

function Router() {
  // Dispara page_view a cada mudança de rota + captura UTM no primeiro hit
  useTracking();
  // PerformanceObserver: LCP/CLS/FCP/INP → dataLayer
  usePerfObserver();

  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/">{withShell(Home)}</Route>
        <Route path="/consorcio/veiculos">{withShell(CVeiculos)}</Route>
        <Route path="/consorcio/motos">{withShell(CMotos)}</Route>
        <Route path="/consorcio/imoveis">{withShell(CImoveis)}</Route>
        <Route path="/consorcio/agro">{withShell(CAgro)}</Route>
        <Route path="/consorcio/pesados">{withShell(CPesados)}</Route>
        <Route path="/consorcio/servicos">{withShell(CServicos)}</Route>
        <Route path="/consultor/camila">{withShell(KCamila)}</Route>
        <Route path="/consultor/lucas">{withShell(KLucas)}</Route>
        <Route path="/consultor/liliane">{withShell(KLiliane)}</Route>
        <Route path="/politica-privacidade">
          {withShell(PoliticaPrivacidade)}
        </Route>
        <Route path="/termos-de-uso">{withShell(Termos)}</Route>
        <Route path="/contato">{withShell(Contato)}</Route>
        <Route path="/obrigado">{withShell(Obrigado)}</Route>
        <Route path="/404">{withShell(NotFound)}</Route>
        <Route>{withShell(NotFound)}</Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  const globalJsonLd = useMemo(
    () => [
      { id: "org", data: buildOrganizationLD() },
      { id: "localbusiness", data: buildLocalBusinessLD() },
      { id: "website", data: buildWebSiteLD() },
    ],
    []
  );

  return (
    <LazyMotion features={domAnimation}>
      <MotionConfig reducedMotion="user">
        <ErrorBoundary>
          <ThemeProvider defaultTheme="light">
            <ConsentProvider>
              <TooltipProvider>
                <SkipToContent />
                <ScrollProgress />
                <GlobalJsonLd entries={globalJsonLd} />
                <Toaster />
                <Router />
                <CookieBanner />
              </TooltipProvider>
            </ConsentProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </MotionConfig>
    </LazyMotion>
  );
}

export default App;
